import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getHighlightedText } from "@/utils/highlight";
import {
    Newspaper,
    Search,
    Calendar,
    User,
    Tag,
    ArrowUpRight,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    TrendingUp,
    Filter,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
} from "../../components/ui/AnimatedSection";
import { NewsArticle } from "@/types/news.types";
import { newsService } from "@/services/news.service";

// Dynamically generated categories from news data
// Will be extracted from fetched news

const sortOptions = [
    { value: "latest", label: "Latest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "title", label: "Title (A-Z)" },
];

const ITEMS_PER_PAGE = 6;

export default function NewsPage() {
    const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [sortBy, setSortBy] = useState("latest");
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch news on mount
    useEffect(() => {
        const fetchNews = async () => {
            try {
                setLoading(true);
                setError(null);
                const news = await newsService.getAllNews();
                console.log("Fetched news:", news);
                setNewsArticles(news);
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load news"
                );
                console.error("Error fetching news:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, []);

    // Extract unique categories from news
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const categories = [
        "All",
        ...Array.from(
            new Set(newsArticles.map((article) => article.category).filter(Boolean))
        ),
    ];

    // Filter and sort articles
    const filteredAndSortedArticles = useMemo(() => {
        let filtered = newsArticles.filter((article) => {
            const matchesSearch =
                article.title
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                article.excerpt
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase());
            const matchesCategory =
                selectedCategory === "All" ||
                article.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

        // Sort articles
        filtered.sort((a, b) => {
            if (sortBy === "latest") {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            } else if (sortBy === "oldest") {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            } else if (sortBy === "title") {
                return a.title.localeCompare(b.title);
            }
            return 0;
        });

        console.log("Articles loaded:", newsArticles.length, "Filtered:", filtered.length, "Selected category:", selectedCategory, "Categories available:", categories);
        return filtered;
    }, [searchQuery, selectedCategory, sortBy, newsArticles, categories]);

    // Pagination
    const totalPages = Math.ceil(
        filteredAndSortedArticles.length / ITEMS_PER_PAGE
    );
    const paginatedArticles = filteredAndSortedArticles.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Reset to page 1 when filters change
    const handleFilterChange = (
        filterType: "search" | "category" | "sort",
        value: string
    ) => {
        setCurrentPage(1);
        if (filterType === "search") setSearchQuery(value);
        else if (filterType === "category") setSelectedCategory(value);
        else if (filterType === "sort") setSortBy(value);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <div className="overflow-hidden">
            {/* Hero */}
            <section className="relative py-32 bg-cream">
                <div className="absolute inset-0 bg-pattern" />
                <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-[#d4b86a]/5 to-transparent" />

                <div className="container-custom px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="badge mb-4">
                            <Sparkles className="w-3 h-3 mr-2" />
                            Latest News & Insights
                        </span>
                        <h1 className="text-gray-900 mb-6">
                            Stay Informed with
                            <span className="block mt-1 gradient-text">
                                Expert Financial Insights
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Get the latest updates on investments, market
                            trends, tax planning, and financial strategies from
                            our expert team.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Search and Filters */}
            <section className="section-padding bg-white border-b border-gray-100">
                <div className="container-custom">
                    <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between mb-8">
                        {/* Search Bar */}
                        <div className="relative flex-1 max-w-md w-full">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) =>
                                    handleFilterChange("search", e.target.value)
                                }
                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/20 focus:border-[#c9a84c] transition-all"
                            />
                        </div>

                        {/* Sort Dropdown */}
                        <div className="flex items-center space-x-3">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <select
                                value={sortBy}
                                onChange={(e) =>
                                    handleFilterChange("sort", e.target.value)
                                }
                                className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/20 focus:border-[#c9a84c] transition-all cursor-pointer"
                            >
                                {sortOptions.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Category Filters */}
                    <div className="flex flex-wrap gap-3">
                        {categories.map((category) => (
                            <motion.button
                                key={category}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() =>
                                    handleFilterChange("category", category)
                                }
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedCategory === category
                                    ? "bg-[#c9a84c] text-white shadow-md"
                                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                    }`}
                            >
                                {category}
                            </motion.button>
                        ))}
                    </div>

                    {/* Results Count */}
                    <div className="mt-6 text-sm text-gray-500">
                        Showing {paginatedArticles.length} of{" "}
                        {filteredAndSortedArticles.length} articles
                    </div>
                </div>
            </section>

            {/* News Grid */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    {loading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="py-16 text-center"
                        >
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="glass-card-hover rounded-xl overflow-hidden h-full flex flex-col animate-pulse"
                                    >
                                        <div className="h-48 bg-gray-200" />
                                        <div className="p-6 flex-1 flex flex-col space-y-3">
                                            <div className="h-4 bg-gray-200 rounded w-3/4" />
                                            <div className="h-4 bg-gray-200 rounded w-1/2" />
                                            <div className="h-20 bg-gray-200 rounded" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {error && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="py-16 text-center"
                        >
                            <Newspaper className="w-16 h-16 text-red-300 mx-auto mb-4" />
                            <h3 className="text-xl font-display text-gray-900 mb-2">
                                Error Loading News
                            </h3>
                            <p className="text-gray-600 mb-6">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="inline-flex items-center space-x-2 bg-[#c9a84c] text-white px-6 py-3 rounded-lg hover:bg-[#b8973e] transition-colors"
                            >
                                <span>Retry</span>
                            </button>
                        </motion.div>
                    )}

                    {!loading && !error && (
                        <AnimatePresence mode="wait">
                            {paginatedArticles.length > 0 ? (
                                <motion.div
                                    key={`${selectedCategory}-${sortBy}-${currentPage}`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {paginatedArticles.map((article) => (
                                            <StaggerItem key={article.id}>
                                                <Link
                                                    to={`/news/${article.slug}`}
                                                    className="group block h-full"
                                                >
                                                    <motion.article
                                                        whileHover={{ y: -6 }}
                                                        className="glass-card-hover rounded-xl overflow-hidden h-full flex flex-col cursor-pointer transition-all"
                                                    >
                                                        {/* Image */}
                                                        <div className="relative overflow-hidden">
                                                            <img
                                                                src={article.image}
                                                                alt={article.title}
                                                                className="w-full h-auto object-contain transition-transform duration-300 group-hover:scale-105"
                                                            />
                                                            <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                                                                <span className="text-xs font-medium text-gray-700 flex items-center">
                                                                    <Tag className="w-3 h-3 mr-1 text-[#c9a84c]" />
                                                                    {article.category}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Content */}
                                                        <div className="p-6 flex-1 flex flex-col">
                                                            <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                                                                <span className="flex items-center">
                                                                    <Calendar className="w-3 h-3 mr-1" />
                                                                    {formatDate(
                                                                        article.date
                                                                    )}
                                                                </span>
                                                                <span className="flex items-center">
                                                                    <User className="w-3 h-3 mr-1" />
                                                                    {article.author}
                                                                </span>
                                                            </div>

                                                            <h3 className="text-lg font-display text-gray-900 mb-2 line-clamp-2 group-hover:text-[#c9a84c] transition-colors">
                                                                {getHighlightedText(article.title, searchQuery)}
                                                            </h3>

                                                            <p className="text-sm text-gray-600 mb-4 flex-grow line-clamp-3">
                                                                {getHighlightedText(article.excerpt, searchQuery)}
                                                            </p>

                                                            <div className="inline-flex items-center space-x-2 text-[#c9a84c] group-hover:text-[#b8973e] font-medium text-sm transition-colors mt-auto">
                                                                <span>Read More</span>
                                                                <ArrowUpRight className="w-4 h-4" />
                                                            </div>
                                                        </div>
                                                    </motion.article>
                                                </Link>
                                            </StaggerItem>
                                        ))}
                                    </StaggerContainer>
                                </motion.div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-center py-16"
                                >
                                    <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-xl font-display text-gray-900 mb-2">
                                        No articles found
                                    </h3>
                                    <p className="text-gray-600">
                                        Try adjusting your search or filter criteria
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}

                    {/* Pagination */}
                    {!loading && !error && totalPages > 1 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-12 flex items-center justify-center space-x-2"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() =>
                                    setCurrentPage((p) => Math.max(1, p - 1))
                                }
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5 text-gray-600" />
                            </motion.button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(
                                    (page) =>
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 &&
                                            page <= currentPage + 1)
                                )
                                .map((page, index, array) => (
                                    <>
                                        {index > 0 &&
                                            array[index - 1] !== page - 1 && (
                                                <span
                                                    key={`ellipsis-${page}`}
                                                    className="px-2 text-gray-400"
                                                >
                                                    ...
                                                </span>
                                            )}
                                        <motion.button
                                            key={page}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-10 h-10 rounded-lg font-medium transition-all ${currentPage === page
                                                ? "bg-[#c9a84c] text-white shadow-md"
                                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                                }`}
                                        >
                                            {page}
                                        </motion.button>
                                    </>
                                ))}

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() =>
                                    setCurrentPage((p) =>
                                        Math.min(totalPages, p + 1)
                                    )
                                }
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                            </motion.button>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 section-dark">
                <div className="container-custom px-6 text-center relative z-10">
                    <AnimatedSection>
                        <h2 className="text-white mb-4">
                            Want Personalized{" "}
                            <span className="gradient-text">
                                Financial Advice
                            </span>
                            ?
                        </h2>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Connect with our expert advisors for tailored
                            financial solutions
                        </p>
                        <Link to="/contact">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary inline-flex items-center space-x-2"
                            >
                                <span>Schedule Consultation</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </motion.button>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
}
